<template>
  <!-- for scroll position -->
  <div ref="scrollToElement" />
  <filter-header
    :show-filter-mode="showFilterMode"
    :toggle-show-mode="toggleShowMode"
    :has-filter="hasFilter"
    :clear-all-filter="clearAllFilter"
    :total-record="publicationStats?.page?.totalRecord"
    :spacing-class="'px-0'"
  >
    <template #list>
      <div
        class="is-flex-desktop is-justify-content-space-between is-align-items-center"
      >
        <span class="mr-4 has-text-weight-bold">View</span>
        <button
          aria-label="change to grid mode"
          :class="{'active': !isListMode}"
          class="button mr-4 py-0 px-2 icon-button"
          @click="isListMode = false"
        >
          <img
            itemprop="logo"
            alt="grid view"
            src="@/assets/grid.svg?url"
          >
        </button>
        <button
          aria-label="change to list view"
          :class="{'active': isListMode}"
          class="button mr-4 py-0 px-2 icon-button"
          @click="isListMode = true"
        >
          <img
            itemprop="logo"
            alt="list view"
            src="@/assets/list.svg?url"
          >
        </button>
        <div class="field is-grouped">
          <div class="control is-expanded sort-select">
            <basic-dropdown
              v-model="queryParam.sortBy"
              :options="sortOptions"
              :label="$t('publication.column.sortBy')"
              @update:model-value="updateFilter"
            />
          </div>
        </div>
      </div>
    </template>
    <template #query-fields>
      <div class="control">
        <CustomDropdown
          v-model="queryParam.subject"
          :button-type="1"
          :options="tagOptions?.subjectMap"
          :label="$t('publication.column.subject')"
          :stats="publicationStats?.subject"
          @update:model-value="updateFilter"
        />
      </div>
      <div class="control">
        <CustomDropdown
          v-model="queryParam.documentType"
          :button-type="2"
          :options="tagOptions?.documentTypeOption"
          :label="$t('publication.column.documentType')"
          :stats="publicationStats?.documentType"
          @update:model-value="updateFilter"
        />
      </div>
      <div class="control">
        <basic-dropdown
          v-model="queryParam.year"
          :options="tagOptions?.yearOptions"
          :label="$t('publication.column.year')"
          @update:model-value="updateFilter"
        />
      </div>
      <div class="control is-expanded searchWrap">
        <selection-input
          v-model="queryParam.searchText"
          :label="$t('common.search')"
          @update:model-value="updateSearchKey"
        />
      </div>
    </template>
    <template #query-display>
      <ShowQueryOptionList
        :lookup-label="tagOptions?.subjectMap"
        :selection="queryParam?.subject"
        :to-split-label="true"
        :class-style="'filter subject'"
        :remove-item="removeSingleQueryItem"
      />
      <ShowQueryOptionList
        :lookup-label="tagOptions?.documentMap"
        :selection="queryParam?.documentType"
        :remove-item="removeSingleQueryItem"
        :class-style="'filter document-type'"
      />
      <template
        v-for="(fieldLabel, fieldKey) in displayLabelForQuery"
        :key="'query' + fieldKey"
      >
        <ShowQueryText
          :field-key="fieldKey"
          :label="fieldLabel"
          :query-param="queryParam"
          :clear-query-item="clearQueryItem"
          :class-style="'tag is-light filter search-term'"
        />
      </template>
    </template>
  </filter-header>
  <section class="section pt-5 px-0">
    <div
      class="container pt-2"
    >
      <template
        v-for="(publication, index) in publicationList"
        :key="'publication' + index"
      >
        <SinglePublicationItem
          :item="publication"
          :default-image-url="defaultImageUrl"
          :is-list-mode="isListMode"
          :child-page-url="childPageUrl"
          :search-text="queryParam.searchText"
          :is-last="publicationList !== undefined
            ? index === publicationList.length - 1
            : false"
        />
      </template>
      <div class="h3" v-if="!publicationList || publicationList?.length === 0">
        {{ $t("publication.list.empty") }}
      </div>
    </div>
    <common-pagination
      :total-page-in-view="pageInView"
      :total-page="publicationStats.page?.totalPage ?? 1"
      :current-page-number="queryParam?.page ?? 1"
      :additional-action="scrollToTop"
      :update-page="updatePage"
    />
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { publicationSearchState } from '@/composables/state/publicationSearchApp';
import SinglePublicationItem from './SinglePublicationItem.vue';
import FilterHeader from '../../components/FilterHeader.vue';
import CustomDropdown from '../../components/dropdown/CustomDropdown.vue';
import SelectionInput from '../../components/search/SelectionInput.vue';
import ShowQueryOptionList from '../../components/ShowQueryOptionList.vue';
import ShowQueryText from '../../components/ShowQueryText.vue';
import CommonPagination from '../../components/CommonPagination.vue';
import BasicDropdown from '../../components/dropdown/BasicDropdown.vue';
import { sortOptions, displayLabelForQuery } from '@/config/index';

defineProps({
  defaultImageUrl: { type: String, default: '' },
  childPageUrl: { type: String, default: '' }
});

const {
  publicationList, queryParam, tagOptions, publicationStats, removeSingleQueryItem, clearAllFilter, clearQueryItem, updatePage
} = publicationSearchState();
// const searchKey = ref('');
const updateSearchKey = (e:string) => {
  queryParam.value.page = 1;
  queryParam.value.searchText = e;
};

const updateFilter = () => {
  queryParam.value.page = 1;
};

const hasFilter = computed(():boolean => {
  return queryParam.value != null && (
    (queryParam.value?.documentType ?? []).length > 0 || (queryParam.value.subject ?? []).length > 0 || (queryParam.value.searchKey ?? []).length > 0 ||
    queryParam.value.author != null || queryParam.value.tag != null);
});
const pageInView = 5;

const isListMode = ref(false);
const showFilterMode = ref(true);
const scrollToElement = ref<HTMLInputElement | null>(null);
const toggleShowMode = () => {
  showFilterMode.value = !showFilterMode.value;
};
const scrollToTop = () => {
  if (scrollToElement.value) {
    scrollToElement.value.scrollIntoView({ behavior: 'smooth' });
  }
};

</script>
<style lang="postcss" scoped>
section.publicationFilters .filterWrap .control:nth-child(3) {
      width: 20%;
  }
  section.publicationFilters .control:nth-child(4) {
    width: 30%;
  }
  .sort-select {
    width: 250px;
  }
  section.publicationFilters .hide {
    display: none!important;
  }

</style>
